/**
 * MapTab: where everyone is, at full width.
 *
 * Rendered by the Team page for canViewWorkspace holders. The care-manager
 * pins come from this week's capacity read (the same rows the board
 * shows), each pin's stance the one the server sent, and none on a row
 * sent without it; the Clients layer is offered to canReadClinicalRecord holders and
 * the server scopes the pins to the records the caller may open. The
 * panel's controls (the client search, layer toggles, unit filter, pick a
 * care manager) sit in a top bar, the map fills the rest, and the
 * not-placed list sits under it.
 *
 * Once the capacity read has data the map stays mounted: a failed refetch
 * shows its banner above the live map, never in its place. The week
 * follows the clock, and while a new week loads (after Monday 00:00) the
 * previous week's rows stay on the map, so the map is neither rebuilt nor
 * refitted and its pins redraw when the new week lands.
 *
 * ?prospect=<work item id> (the intake card's Show on map) opens Place a
 * client on that card once: the parameter is read when the tab mounts,
 * only for a viewer the Clients layer is offered to, and removed from the
 * URL on arrival whoever the viewer is, so closing the panel, changing
 * tab and coming back, or reloading never opens it again.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canManageClientIdentity, canManageWorkItems, canReadClinicalRecord } from '@/lib/capabilities';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SkeletonRow } from '@/components/Skeleton';
import { CapacityMapPanel } from '@/components/capacity/CapacityMapPanel';
import { currentWeekMonday } from '@/lib/capacity';
import { useClientMap, useCmCapacity } from '@/lib/capacityApi';
import { useAssignClientCm } from '@/lib/clientsApi';
import { useReassignCm } from '@/lib/workspace';
import { useUnitCoverage } from '@/lib/unitCoverage';
import { TEAM_MAP_PROSPECT_PARAM } from '@/lib/clientLocation';

export interface MapTabProps {
  tenantSlug: string;
}

/** Tall enough to be the page: the viewport less the chrome, band and tab row. */
const FULL_MAP_HEIGHT = 'h-[calc(100vh-22rem)] min-h-[28rem]';

export function MapTab({ tenantSlug }: MapTabProps): ReactNode {
  const { whoami } = useTenant();
  const canShowClients = canReadClinicalRecord(whoami);
  const canAssignProspect = canManageWorkItems(whoami);
  const canAssignClient = canManageClientIdentity(whoami);
  const [searchParams, setSearchParams] = useSearchParams();
  const [landingProspectId] = useState<string | null>(() =>
    canShowClients ? searchParams.get(TEAM_MAP_PROSPECT_PARAM) : null,
  );
  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (!prev.has(TEAM_MAP_PROSPECT_PARAM)) return prev;
        const next = new URLSearchParams(prev);
        next.delete(TEAM_MAP_PROSPECT_PARAM);
        return next;
      },
      { replace: true },
    );
    // Once, on arrival: the parameter never outlives the landing.
  }, []);
  const capacity = useCmCapacity(tenantSlug, currentWeekMonday(), { keepPreviousWeek: true });
  const clientMap = useClientMap(tenantSlug, { enabled: canShowClients });
  const { units: unitCoverage } = useUnitCoverage(tenantSlug);
  const reassignCm = useReassignCm(tenantSlug);
  const assignClientCm = useAssignClientCm(tenantSlug);

  return (
    <div data-testid="map-tab">
      {capacity.data === null && capacity.isLoading && (
        <div><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>
      )}
      {capacity.isError && <ErrorBanner error={capacity.error} onRetry={capacity.refetch} what="the map" />}
      {capacity.data !== null && (
        <CapacityMapPanel
          tenantSlug={tenantSlug}
          careManagers={capacity.careManagers}
          clientMap={clientMap.data}
          canShowClients={canShowClients}
          clientsLoading={clientMap.isLoading}
          clientsError={clientMap.isError}
          mapHeightClass={FULL_MAP_HEIGHT}
          unitCoverage={unitCoverage}
          canAssignProspect={canAssignProspect}
          canAssignClient={canAssignClient}
          initialProspectId={landingProspectId}
          onAssignProspect={async (workItemId, rosterId) => {
            await reassignCm.mutateAsync({ id: workItemId, rosterId });
          }}
          onAssignClient={async (clientId, rosterId) => {
            await assignClientCm.mutateAsync({ id: clientId, rosterId });
          }}
        />
      )}
    </div>
  );
}
